import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(d.getTime())) return 'Invalid Date';

  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

/**
 * 格式化数字显示
 * @param num 数字或字符串
 */
export function formatNumber(num: number | string): string {
  const value = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(value) || value === null || value === undefined) {
    return '0';
  }
  
  // 大于等于1万，显示为万
  if (Math.abs(value) >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }

  // 小于1万，显示原始数字（带千分位）
  return Math.round(value).toLocaleString();
}

/**
 * 从各种格式的B站直播间链接中提取房间号
 * 支持的格式：
 * 1. 标准链接：https://live.bilibili.com/12151156
 * 2. 带参数的链接：https://live.bilibili.com/12151156?popular_rank=1&live_from=71005
 * 3. H5链接：https://live.bilibili.com/h5/30894795?broadcast_type=0
 * 4. 裸房间号：12151156
 * @param input 用户输入的链接或房间号
 * @returns 提取的房间号，如果无法提取则返回null
 */
export function extractRoomId(input: string): string | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  const trimmedInput = input.trim();

  // 情况1：纯数字房间号
  if (/^\d+$/.test(trimmedInput)) {
    return trimmedInput;
  }

  // 情况2：标准桌面链接 https://live.bilibili.com/12151156
  // 可能带参数：https://live.bilibili.com/12151156?popular_rank=1&live_from=71005&visit_id=xxx
  const desktopMatch = trimmedInput.match(/live\.bilibili\.com\/(\d+)/);
  if (desktopMatch) {
    return desktopMatch[1];
  }

  // 情况3：H5移动端链接 https://live.bilibili.com/h5/30894795
  // 可能带参数：https://live.bilibili.com/h5/30894795?broadcast_type=0&is_room_feed=1
  const h5Match = trimmedInput.match(/live\.bilibili\.com\/h5\/(\d+)/);
  if (h5Match) {
    return h5Match[1];
  }

  // 无法识别的格式
  return null;
}

/**
 * 验证并规范化B站直播间URL
 * @param input 用户输入的链接或房间号
 * @returns 规范化后的标准URL，如果无法解析则返回null
 */
export function normalizeBilibiliLiveUrl(input: string): string | null {
  const roomId = extractRoomId(input);
  
  if (!roomId) {
    return null;
  }

  // 返回标准格式的URL
  return `https://live.bilibili.com/${roomId}`;
}

